import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Coins, Gamepad2, Compass, Star, Music, PartyPopper, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import Reveal from "@/components/site/Reveal";
import SectionHeading from "@/components/site/SectionHeading";

const PERKS = [
  { icon: Coins,       tone: "am-tile-lemon",   title: "Earn RPM tokens",       copy: "Rack up rewards for every minute you listen, watch, or play." },
  { icon: Compass,     tone: "am-tile-mint",    title: "Discover deeper",       copy: "LiveLink identifies music anywhere and unlocks bonus content." },
  { icon: Gamepad2,    tone: "am-tile-pink",    title: "Play the lyrics",       copy: "Bubble games sync to the beat — highest scores earn the biggest RPM." },
  { icon: Sparkles,    tone: "am-tile-lavender",title: "Redeem for treasures",  copy: "Cash-reward auctions, merch, exclusive artist drops." },
  { icon: Star,        tone: "am-tile-peach",   title: "Back real artists",     copy: "Your engagement directly rewards the creators you love." },
  { icon: PartyPopper, tone: "am-tile-coral",   title: "Join the community",    copy: "Meet fans who dig deeper. Share tips. Level up together." },
];

const ALBUMS = [
  { tone: "am-tile-pink",     title: "Midnight Loop",  artist: "Neon Static" },
  { tone: "am-tile-peach",    title: "Solar Bloom",    artist: "Kai River"  },
  { tone: "am-tile-mint",     title: "Coastal Hymns",  artist: "Lila Sea"   },
  { tone: "am-tile-lavender", title: "Neon Sundays",   artist: "Prime Fold" },
  { tone: "am-tile-lemon",    title: "Golden Hour",    artist: "Hey Compass"},
  { tone: "am-tile-sky",      title: "Skyline Radio",  artist: "The Verge"  },
];

export default function ForFans() {
  return (
    <main data-testid="page-for-fans" className="pt-24 md:pt-32">
      {/* HERO */}
      <section className="relative">
        <div className="container relative">
          <div className="am-blob" style={{ left: "-140px", top: "20px" }} />
          <div className="am-blob am-blob-lavender" style={{ right: "-140px", top: "100px" }} />

          <div className="flex flex-col items-center text-center gap-8 max-w-4xl mx-auto relative z-10">
            <Reveal>
              <span className="inline-flex items-center gap-2 rounded-full bg-white/80 backdrop-blur px-4 py-1.5 text-[11px] uppercase tracking-[0.24em] text-primary font-semibold border border-black/5">
                <Music className="h-3.5 w-3.5" /> For Fans
              </span>
            </Reveal>
            <Reveal delay={80}>
              <h1 className="font-display font-bold tracking-tight text-[clamp(2.6rem,7vw,6rem)] leading-[0.98]">
                Your ears just got a <span className="am-sunset-text">paycheck.</span>
              </h1>
            </Reveal>
            <Reveal delay={160}>
              <p className="text-lg md:text-xl text-foreground/65 max-w-2xl">
                Streaming platforms treat you like a product. AudioMine treats you like a partner. Every listen, every skip-back, every game — you're mining RPM you can actually cash in.
              </p>
            </Reveal>
            <Reveal delay={220}>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact?type=fan">
                  <Button data-testid="fans-cta-join" size="lg" className="rounded-full h-14 px-8 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-base shadow-[0_10px_30px_-8px_rgba(250,37,104,0.55)]">
                    Join the waitlist <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/how-it-works">
                  <Button size="lg" variant="outline" className="rounded-full h-14 px-8 border-black/10 bg-white hover:bg-white/70 text-foreground font-semibold text-base">
                    How mining works
                  </Button>
                </Link>
              </div>
            </Reveal>
          </div>

          {/* Album grid */}
          <Reveal delay={280}>
            <div className="mt-16 grid grid-cols-3 md:grid-cols-6 gap-4">
              {ALBUMS.map((a, i) => (
                <div key={a.title} className={`relative aspect-square rounded-[20px] ${a.tone} p-4 flex flex-col justify-end overflow-hidden group cursor-pointer`}>
                  <div className="absolute right-3 top-3 h-8 w-8 rounded-full bg-white/70 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Play className="h-3.5 w-3.5 fill-primary text-primary" />
                  </div>
                  <div className="text-[10px] md:text-xs font-semibold text-foreground truncate">{a.title}</div>
                  <div className="text-[9px] md:text-[10px] text-foreground/60 truncate">{a.artist}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* PERKS */}
      <section className="py-24 md:py-32">
        <div className="container">
          <SectionHeading
            eyebrow="What's in it for you"
            title={<>Six reasons fans <span className="font-serif-editorial italic font-normal">stay.</span></>}
            subtitle="Music discovery, gamification, and real rewards — bundled into one full-fidelity experience."
          />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PERKS.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={p.title} delay={i * 80}>
                  <div className="am-soft p-8 h-full flex flex-col gap-5">
                    <div className={`h-14 w-14 rounded-2xl ${p.tone} flex items-center justify-center text-foreground`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <div className="font-display font-bold text-xl">{p.title}</div>
                    <p className="text-foreground/60 leading-relaxed">{p.copy}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* WAVEFORM BANNER */}
      <section className="py-16 md:py-24">
        <div className="container">
          <div className="rounded-[32px] am-tile-pink p-10 md:p-16 flex flex-col md:flex-row items-center justify-between gap-8 overflow-hidden relative">
            <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-white/25 blur-3xl" />
            <div className="flex items-end gap-1.5 h-16">
              {Array.from({ length: 38 }).map((_, i) => (
                <span
                  key={i}
                  className="am-bar w-1.5 rounded-full bg-white"
                  style={{ height: `${20 + Math.abs(Math.sin(i)) * 44}px`, animationDelay: `${i * 60}ms` }}
                />
              ))}
            </div>
            <div className="text-center md:text-right text-white relative z-10">
              <div className="font-display font-bold text-3xl md:text-4xl">Every beat, rewarded.</div>
              <div className="text-white/80 text-sm mt-2 max-w-md">Full-fidelity D1 Audio + gamified interaction = the deepest listening on the internet.</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="container text-center flex flex-col items-center gap-8">
          <Reveal>
            <h2 className="font-display font-bold text-4xl md:text-6xl leading-[1] max-w-3xl">
              Stop scrolling. <span className="am-sunset-text">Start mining.</span>
            </h2>
          </Reveal>
          <Reveal delay={100}>
            <Link to="/contact?type=fan">
              <Button data-testid="fans-cta-bottom" size="lg" className="rounded-full h-14 px-10 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shadow-[0_16px_40px_-8px_rgba(250,37,104,0.55)]">
                Get on the fan list <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
